"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const DEFAULT_MAX_FILES = 4;
const DEFAULT_MAX_BYTES = 4 * 1024 * 1024;

export type ImageAttachmentPickerProps = {
  files: File[];
  onFilesChange: (files: File[]) => void;
  disabled?: boolean;
  label: React.ReactNode;
  hint: React.ReactNode;
  dropTitle: React.ReactNode;
  dropSubtitle: React.ReactNode;
  maxFiles?: number;
  maxBytes?: number;
  accept?: string;
  onValidationError?: (code: "fileTooLarge" | "fileTypeInvalid") => void;
  removePhotoLabel: string;
  className?: string;
};

export function ImageAttachmentPicker({
  files,
  onFilesChange,
  disabled,
  label,
  hint,
  dropTitle,
  dropSubtitle,
  maxFiles = DEFAULT_MAX_FILES,
  maxBytes = DEFAULT_MAX_BYTES,
  accept = "image/png,image/jpeg,image/jpg,image/webp,image/gif",
  onValidationError,
  removePhotoLabel,
  className,
}: ImageAttachmentPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [previews, setPreviews] = useState<string[]>([]);
  const reactId = useId();
  const inputId = `file-${reactId}`;

  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => {
      for (const u of urls) URL.revokeObjectURL(u);
    };
  }, [files]);

  const addFromList = useCallback(
    (list: FileList | null) => {
      if (!list?.length) return;
      const next: File[] = [...files];
      for (let i = 0; i < list.length && next.length < maxFiles; i++) {
        const f = list.item(i);
        if (!f || !f.size) continue;
        if (f.size > maxBytes) {
          onValidationError?.("fileTooLarge");
          continue;
        }
        if (!/^image\/(png|jpeg|jpg|webp|gif)$/i.test(f.type)) {
          onValidationError?.("fileTypeInvalid");
          continue;
        }
        next.push(f);
      }
      onFilesChange(next);
      if (inputRef.current) inputRef.current.value = "";
    },
    [files, maxBytes, maxFiles, onFilesChange, onValidationError]
  );

  function removeAt(index: number) {
    onFilesChange(files.filter((_, j) => j !== index));
  }

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex flex-col gap-0.5">
        <Label htmlFor={inputId} className="text-sm font-medium leading-none">{label}</Label>
        <p id={`${inputId}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="min-w-0 flex-1">
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            accept={accept}
            multiple
            className="sr-only"
            disabled={disabled}
            onChange={(e) => addFromList(e.target.files)}
          />
          <button
            type="button"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!disabled) setDragOver(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragOver(false);
              if (disabled) return;
              addFromList(e.dataTransfer.files);
            }}
            className={cn(
              "flex w-full flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors",
              "bg-muted/30 hover:bg-muted/50",
              dragOver && "border-primary bg-primary/5",
              !disabled && "cursor-pointer",
              disabled && "pointer-events-none opacity-50"
            )}
            aria-describedby={`${inputId}-hint`}
            aria-label={String(dropTitle)}
          >
            <ImagePlus
              className={cn("h-8 w-8 text-muted-foreground", dragOver && "text-primary")}
              strokeWidth={1.5}
              aria-hidden
            />
            <span className="text-sm font-medium text-foreground">{dropTitle}</span>
            <span className="text-xs text-muted-foreground">{dropSubtitle}</span>
          </button>
        </div>

        {files.length > 0 ? (
          <ul className="grid w-full grid-cols-2 gap-2 sm:max-w-[220px] sm:grid-cols-1">
            {files.map((f, i) => (
              <li
                key={`${f.name}-${i}`}
                className="group relative overflow-hidden rounded-md border bg-background shadow-sm"
              >
                <div className="aspect-video w-full bg-muted">
                  {previews[i] ? (
                    <img
                      src={previews[i]}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : null}
                </div>
                <p className="truncate px-2 py-1.5 text-[11px] text-muted-foreground" title={f.name}>
                  {f.name}
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="absolute right-1 top-1 h-7 w-7 rounded-full bg-background/90 shadow-sm opacity-0 transition-opacity group-hover:opacity-100"
                  onClick={() => removeAt(i)}
                  disabled={disabled}
                  aria-label={removePhotoLabel}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
